import { mergePlain } from 'util/merge'
import { doGraphqlRequest } from 'ducks/requests/operations'
import { buildId } from 'util/globalId'
import { withUndoSend } from 'ducks/undoSend/request'
import { CONVERSATION_FORWARD } from '../actionTypes'
import { updateConversationForwardMutation } from '../mutations'
import { CONVERSATION_SAVE_UNLOAD_MESSAGE, SNOOZED } from '../constants'
import { conversationForwardGraphQlResponseSchema } from '../schema'
import { selectCurrentConversationById } from '../selectors'
import {
  onUpdateAttachEventGroupIdToEvents,
  withAutoRedirect,
  withBeforeSend,
} from '../utils/request'
import { buildConversationOptimistRequestOptions } from '../utils/optimistic'
import { selectWillAutoRedirectForTicketId } from '../selectors/selectWillAutoRedirectForTicketId'
import { throwOnMissingConversationOrErrors } from './graphqlSemanticFailure'
import {
  attachmentsToEmailAttachmentInput,
  parseToApiSnoozeUntil,
} from '../utils/payload'

export const doForward = (
  ticketId,
  inputBody,
  to,
  cc,
  bcc,
  subject,
  inputAttachments,
  inputCannedReplyId,
  draftId,
  inputState,
  inputSnoozeUntil,
  tagIdsToAdd,
  tagIdsToRemove,
  removeAllTags,
  inputAgentId,
  inputTeamId,
  inputChannelId,
  options = {}
) => async (dispatch, getState) => {
  const attachments = attachmentsToEmailAttachmentInput(inputAttachments)
  const conversationId = buildId('Conversation', ticketId)
  const agentId = buildId('Agent', inputAgentId)
  const teamId = buildId('Team', inputTeamId)
  const channelId = buildId('Channel', inputChannelId)
  const cannedReplyId = buildId('CannedReply', inputCannedReplyId)

  const store = getState()
  const ticket = selectCurrentConversationById(store, ticketId)
  const snoozeUntil = parseToApiSnoozeUntil(
    inputState === SNOOZED
      ? inputSnoozeUntil || ticket.snoozed?.until
      : inputSnoozeUntil
  )
  const state = inputSnoozeUntil ? SNOOZED : inputState
  const body = inputBody || ''
  if (!ticket || !to || !to[0]) return null

  const {
    optimist,
    additionalActions,
  } = await buildConversationOptimistRequestOptions(
    getState,
    ticketId,
    {
      conversationId: ticketId,
      subject,
      state,
      snoozeUntil,
      tagIdsToAdd,
      tagIdsToRemove,
      removeAllTags,
      agentId,
      teamId,
      channelId,
      forward: {
        body,
        attachments: inputAttachments,
        to,
        cc,
        bcc,
      },
    },
    options
  )

  const backHref = window.location.href
  const willAutoRedirect = selectWillAutoRedirectForTicketId(
    getState(),
    ticketId,
    'reply',
    !!options?.moduleOptions?.autoRedirect?.forced
  )

  return dispatch(
    withUndoSend(
      draftId,
      withBeforeSend(
        ticketId,
        draftId,
        withAutoRedirect(
          ticketId,
          (afterStartedActions, afterSuccessActions) => async () =>
            dispatch(
              doGraphqlRequest(
                CONVERSATION_FORWARD,
                updateConversationForwardMutation(),
                {
                  conversationId,
                  body,
                  to,
                  cc,
                  bcc,
                  subject,
                  attachments,
                  cannedReplyId,
                  draftId,
                  state,
                  snoozeUntil,
                  tagIdsToAdd,
                  tagIdsToRemove,
                  removeAllTags,
                  agentId,
                  teamId,
                  channelId,
                  skipNotifications: !!options.skipNotifications,
                },
                mergePlain(
                  {
                    app: true,
                    throwOnError: true,
                    concurrency: {
                      key: ticketId,
                      message: CONVERSATION_SAVE_UNLOAD_MESSAGE,
                    },
                    normalizationSchema: conversationForwardGraphQlResponseSchema,
                    transformResponse: data =>
                      onUpdateAttachEventGroupIdToEvents(
                        'conversationForwardV2'
                      )(
                        throwOnMissingConversationOrErrors(
                          data,
                          'conversationForwardV2'
                        )
                      ),
                    optimist,
                    moduleOptions: {
                      snackbar: {
                        enabled: true,
                        started: {
                          enabled: true,
                          message: 'Forwarding your reply...',
                        },
                        success: {
                          enabled: true,
                          message: `Your email has been sent.`,
                          link: willAutoRedirect && {
                            href: backHref,
                            text: `Go back to ${app.t('Ticket')}`,
                          },
                        },
                        failed: {
                          enabled: true,
                          message: `Oops, we couldn't forward your reply. Please try again in a moment.`,
                        },
                      },
                      entities: {
                        additionalActions,
                      },
                    },
                    meta: {
                      mergeEntities: true,
                    },
                    afterStartedActions,
                    afterSuccessActions,
                  },
                  options
                )
              )
            ),
          mergePlain(
            {
              moduleOptions: {
                autoRedirect: {
                  enabled: true,
                  operation: 'reply',
                },
              },
            },
            options
          )
        )
      )
    )
  )
}
